// First: check HTTP status
import {toast} from "react-toastify";
import {Exception} from "@zxing/library";

export async function handleFetchResponseStatus(response: Response, printError = true): Promise<Response> {
    const {status} = response;

    if (status === 401) {
        toast.error("Ви не авторизовані, буде виконано перенаправлення на сторінку входу");
        window.location.href = '/login.html';
        return Promise.reject(new Exception("Unauthorized"));
    }

    if (status >= 400) {
        const contentType = response.headers.get("content-type") ?? "";

        if (contentType.includes("application/json")) {
            try {
                const json = await response.json();
                printError && toast.error(json.message || json["errorBody"] || "Сталась помилка при виконанні мережевого запиту");
                return Promise.reject(json);
            } catch {
                printError && toast.error(`HTTP ${status} - Failed to parse JSON`);
                return Promise.reject(new Exception(`HTTP ${status} - Failed to parse JSON`));
            }
        }

        if (contentType.includes("text/")) {
            try {
                const text = await response.text();
                printError && toast.error(text);
                return Promise.reject(new Exception(text));
            } catch {
                printError && toast.error(`HTTP ${status} - Failed to parse text`);
                return Promise.reject(new Exception(`HTTP ${status} - Failed to parse text`));
            }
        }

        printError && toast.error(`HTTP ${status} ${response.statusText}`);
        return Promise.reject(new Exception(`HTTP ${status} ${response.statusText}`));
    }

    // Success
    return Promise.resolve(response);
}

// Second: check JSON payload status
export async function handleFetchJson<T = any>(response: Response, printError = true): Promise<{
    success: boolean,
    body: T,
    message?: string
}> {
    const contentType = response.headers.get("content-type") ?? "";

    if (!contentType.includes("application/json")) {
        return Promise.reject(new Exception("Invalid response type: expected JSON"));
    }

    try {
        const data: any = await response.json();
        const res = {success: data?.status === "success", body: data, message: data.message}
        if (!data?.error && res.success) {
            return res;
        }

        if (data?.error && printError)
            toast.error(
                data?.message ||
                data?.["errorBody"] ||
                "Сталась помилка при виконанні мережевого запиту"
            );
        return res;
    } catch (e: any) {
        toast.error(e.message);
        return Promise.reject(new Exception(e.message));
    }
}

export type ApiResult<T> = {
    error: string;
    success: boolean,
    body: T;
    message?: string
};

export function isApiError<T>(result: ApiResult<T>): result is ApiResult<T & Exception> {
    return !!result.error && result.body instanceof Exception;
}

export async function apiFetch<T = { data: { [key: string]: any } | any[] | any } & { [key: string]: any }>(
    url: string,
    options: RequestInit = {},
    toastError = true,
    onError?: <U = T>(error: any) => U | Promise<U>
): Promise<ApiResult<T>> {
    try {
        const response = await fetch(url, {
            credentials: "include",
            ...options,
        });
        const r = await handleFetchResponseStatus(response, toastError);
        const json = await handleFetchJson<T>(r, toastError);
        return {error: '', success: json.success, body: json.body, message: json.message};
    } catch (err: any) {
        // Silently ignore aborted fetches
        if (err.name === "AbortError") {
            console.debug("aborted fetch", url);
            return {error: 'aborted', success: false, body: null as any};
        }

        console.debug("apiFetch error:", err);

        if (onError) try {
            const result = await onError(err);
            if (result !== undefined) return {error: err.toString(), success: false, body: result as T};
        } catch (handlerErr) {
            console.debug("Error inside onError handler:", handlerErr);
        }

        return {error: err.toString(), success: false, body: err as T};
    }
}

type ConfirmOptions<T = any, K = any> = {
    message: string;
    onConfirm: () => T | Promise<T>;
    onReject?: () => K | Promise<K>;
};

export function confirmAction<T = any, K = any>({
                                                    message,
                                                    onConfirm,
                                                    onReject
                                                }: ConfirmOptions<T, K>): Promise<T | K | undefined> {
    return new Promise<T | K | undefined>(async (resolve, reject) => {
        const confirmed = window.confirm(message);

        if (confirmed) {
            try {
                const result = await onConfirm();
                resolve(result);
            } catch (err) {
                console.error("Error in onConfirm:", err);
                reject(err);
            }
        } else if (onReject) {
            try {
                const rejectResult = await onReject();
                resolve(rejectResult);
            } catch (err) {
                console.error("Error in onReject:", err);
                reject(err);
            }
        } else {
            resolve(undefined);
        }
    });
}
